import { getLiveOffer, promoBadgeLabel, resolveTiers, tierForAmount } from '@theme/sd-intelligems-offer';

/**
 * SD Promo Badge (WEB-387)
 *
 * During a Buy More Save More promotion, marks each product card with the discount that
 * item's own price already earns in cart: "Save 20%+". The "+" says a fuller cart can
 * unlock a higher tier, so it comes off the top tier ("Save 30%"). Wording is Grace and
 * Cory's, 2026-09-22; see `promoBadgeLabel`.
 *
 * Which shoppers see it, and in what colours, is an Intelligems test rather than a theme
 * setting: `snippets/sd-promo-badge-config.liquid` serialises the operator's metaobject
 * entries, and the variant the shopper is bucketed into decides the colours or suppresses
 * the badge entirely (that is how a control variant is expressed).
 *
 * Tiers are per-shopper A/B-resolved and cannot be server-rendered, so the badge arrives
 * after Intelligems does — the same async reveal the promotion tracker has. It shares that
 * component's offer reading through `@theme/sd-intelligems-offer` so the two can never
 * disagree about which offer is live or where a tier starts.
 *
 * Every gate below must pass, in order, or no badge is inserted. They are all re-evaluated
 * on each decoration pass, so the badge disappears by itself when a promo ends, a test
 * stops, or the config is switched off — nothing has to clean up after it.
 */

const BADGE_CLASS = 'sd-product-badge';
const PROMO_CLASS = 'sd-product-badge--promo';
const LIST_SELECTOR = 'ul[data-promo-badge]';

/** @typedef {{variantId: string, enabled: boolean, backgroundColor: string|null, textColor: string|null}} PromoVariant */
/** @typedef {{testId: string, variants: PromoVariant[]}} PromoTest */

/**
 * The operator's config, or an empty list when no Promo Badge Test entry is Active — which
 * is every shop until someone activates one. Parsed once; it is server-rendered and static.
 * @returns {PromoTest[]}
 */
function readConfig() {
  const node = document.getElementById('sd-promo-badge-config');
  if (!node?.textContent) return [];
  try {
    const parsed = JSON.parse(node.textContent);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // A malformed config disables the badge rather than throwing on every page.
    return [];
  }
}

/**
 * Intelligems' own API surface is not ours; treat every call as able to throw or be absent.
 * @param {any} obj
 * @param {string} method
 */
function call(obj, method, ...args) {
  try {
    return typeof obj?.[method] === 'function' ? obj[method](...args) : null;
  } catch {
    return null;
  }
}

/**
 * The badge this shopper should see, or null.
 *
 * Gates, in the ticket's order: a configured test, a usable active offer, membership of
 * that test, an enabled variant entry. The tier itself is per-product and resolved later.
 *
 * @param {PromoTest[]} config
 * @returns {{tiers: {value: number, label: string, max: number, discount: number}[], variant: PromoVariant, testId: string} | null}
 */
function resolveBadge(config) {
  if (config.length === 0) return null;

  // One Intelligems offer runs at a time on these brands, so the first usable one is the
  // one the badge describes — no data-offer-id pinning.
  const offer = getLiveOffer()?.offer;
  const { tiers, unitType } = resolveTiers(offer);
  if (tiers.length === 0) return null;

  // A quantity-based offer measures its thresholds in ITEMS, not money, so the badge has
  // nothing to compare a single product's price against — a $150 product is not "3 items".
  // The promotion tracker can serve both bases because it reads the cart (item count or
  // subtotal); a product card only has a price. Without this gate, cents would be tested
  // against thresholds like 3 or 5 and every product would badge the top tier.
  if (unitType !== 'dollar') return null;

  for (const test of config) {
    // `null` means this shopper is not in the test at all, so no badge. Note this is
    // getTestGroup (singular, the shopper's own group), not getTestGroups (plural, every
    // group in the test) — verified live on anolon 2026-09-21.
    const group = call(window.igData?.user, 'getTestGroup', test.testId);
    const groupId = group?.id;
    if (!groupId) continue;

    // Strict `=== true`: a control variant is the one shopper who must never see a badge,
    // so anything other than a real JSON boolean true is treated as off rather than
    // trusted for truthiness. The config snippet emits a real boolean; this is the belt
    // to its braces.
    const variant = test.variants?.find((entry) => entry.variantId === groupId);
    if (variant?.enabled !== true) continue;

    return { tiers, variant, testId: test.testId };
  }

  return null;
}

/**
 * Decorate one badge list, or strip a badge that no longer qualifies.
 *
 * @param {HTMLElement} list
 * @param {ReturnType<typeof resolveBadge>} badge
 */
function decorateList(list, badge) {
  const existing = list.querySelector(`.${PROMO_CLASS}`);

  const excluded = (list.dataset.promoExcluded || '').split(/\s+/).filter(Boolean);
  const price = Number(list.dataset.price);
  const tier = badge && !excluded.includes(badge.testId) ? tierForAmount(badge.tiers, price) : null;
  const text = badge ? promoBadgeLabel(badge.tiers, tier) : null;

  // Every failing gate lands here: no config, no offer, not in the test, a control
  // variant, an excluded collection, a product priced below the first tier, or a tier
  // with no percentage to state (free shipping or a gift only).
  if (!text) {
    existing?.remove();
    return;
  }

  if (existing) {
    // Re-decorating an unchanged badge would restart the entrance animation on every pass.
    if (existing.textContent !== text) existing.textContent = text;
    return;
  }

  const item = document.createElement('li');
  item.className = `${BADGE_CLASS} ${PROMO_CLASS}`;
  item.textContent = text;
  if (badge.variant.backgroundColor) item.style.setProperty('--badge-bg', badge.variant.backgroundColor);
  if (badge.variant.textColor) item.style.setProperty('--badge-text', badge.variant.textColor);

  // Position 1. Liquid's `sort: 'priority'` has already run server-side, so list order is
  // final before this script ever sees it and DOM position is the whole rule.
  list.prepend(item);

  // Anything past the cap is dropped for the duration of the promo.
  const max = Number(list.dataset.maxBadges);
  if (Number.isFinite(max) && max > 0) {
    while (list.children.length > max) list.lastElementChild?.remove();
  }
}

/** Re-evaluate every badge list currently in the document. */
function decorateAll() {
  const badge = resolveBadge(readConfig());
  for (const list of document.querySelectorAll(LIST_SELECTOR)) {
    decorateList(/** @type {HTMLElement} */ (list), badge);
  }
}

/**
 * Cards arrive after first paint by more than one route, and the two that matter destroy
 * or bypass anything already spliced in:
 *
 *   - `assets/paginated-list.js` appends fresh server-rendered cards for a new page, and
 *     routes facet changes through the same component.
 *   - `assets/product-recommendations.js` replaces its entire subtree with
 *     `this.innerHTML = recommendations.innerHTML`, which destroys a spliced badge
 *     outright — the products come back undecorated.
 *
 *   - `assets/predictive-search.js` and Horizon's other `morph` re-renders reconcile an
 *     existing card against fresh server markup in place. The badge is not in that markup,
 *     and morph does not simply remove it: it REUSES our `<li>` for the next badge,
 *     rewriting its class, style and text, then removes the now-surplus last `<li>`, a
 *     plain badge. Our class disappears through an attribute change and the only removed
 *     node is someone else's (logged on anolon 2026-09-23). So the trigger is any child
 *     change inside a badge list, not the promo class leaving the DOM.
 *
 * One observer on the document covers all of them, plus any future re-render path,
 * without editing an upstream Horizon file. That matters here beyond tidiness: writing to
 * `<product-recommendations>` is what caused WEB-375, where the component read its own
 * attribute change as "reload me" and permanently hid the section. Observing from outside
 * touches none of its attributes.
 */
function watchForNewCards() {
  // Coalesce to one pass per frame, the same idiom as the other document-wide observers
  // in this theme (`sd-animations.js`, `sd-shipping-restriction.js`). A page of new
  // results arrives as many mutation records, and every one of them would otherwise run a
  // full re-resolve and re-query.
  let pending = false;
  const schedule = () => {
    pending = true;
    requestAnimationFrame(() => {
      decorateAll();
      pending = false;
    });
  };

  // A badge list arrived, or a badge list's children changed under us. Our own edits to a
  // list (inserting, trimming to the cap, removing when a gate fails) also land here; the
  // re-pass that follows finds every list already in the state it wants and changes
  // nothing, so it settles after one extra frame.
  const concerns = (node) =>
    node instanceof Element && (node.matches(LIST_SELECTOR) || node.querySelector(LIST_SELECTOR) !== null);

  const observer = new MutationObserver((mutations) => {
    if (pending) return;
    for (const mutation of mutations) {
      const inList = mutation.target instanceof Element && mutation.target.closest(LIST_SELECTOR) !== null;
      if (inList || [...mutation.addedNodes].some(concerns)) {
        schedule();
        return;
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

decorateAll();
// Intelligems can load after this script runs; re-resolve and repaint once its data lands.
window.addEventListener('ig:ready', decorateAll);
watchForNewCards();
